import { useRef, useState } from "react";
import type { ZonedTimeString } from "@poi-planner/shared";
import {
  MIN_SPAN_MINUTES,
  SNAP_MINUTES_POINTER,
  SNAP_MINUTES_TOUCH,
  blockGeometry,
  yToZonedTime,
} from "../../utils/timeGrid";
import { addMinutes, epochMs, minutesBetween } from "../../utils/zonedTime";

export type ResizeEdge = "start" | "end";

interface UseBlockResizeOptions {
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
  date: string;
  zone: string;
  disabled: boolean;
  /** Single mutation on pointerup — one undo entry, one autosave debounce */
  onCommit: (
    times: {
      startTime: ZonedTimeString;
      endTime: ZonedTimeString;
    },
    context: {
      edge: ResizeEdge;
      originalDisplayZone: string;
      targetDisplayZone: string;
      targetDate: string;
    },
  ) => void;
  onPreviewChange?: (
    times: { startTime: ZonedTimeString; endTime: ZonedTimeString } | null,
  ) => void;
}

export interface ResizePreview {
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
  top: number;
  height: number;
}

/**
 * Edge-resize gesture for calendar blocks. Raw pointer events with
 * setPointerCapture — never dnd-kit; pointerdown stops propagation so the
 * PointerSensor's 8px activation can't start a block drag mid-resize.
 *
 * The gesture keeps a local preview and commits exactly once on pointerup.
 */
export function useBlockResize({
  startTime,
  endTime,
  date,
  zone,
  disabled,
  onCommit,
  onPreviewChange,
}: UseBlockResizeOptions) {
  const [preview, setPreview] = useState<ResizePreview | null>(null);
  const gesture = useRef<{
    edge: ResizeEdge;
    snap: number;
  } | null>(null);

  const pointerTime = (
    e: React.PointerEvent,
  ): { value: ZonedTimeString; date: string; zone: string } => {
    const hit = document.elementFromPoint(e.clientX, e.clientY);
    const column = hit?.closest<HTMLElement>("[data-calendar-date]");
    const targetDate = column?.dataset.calendarDate ?? date;
    const targetZone = column?.dataset.calendarZone ?? zone;
    const rect = column?.getBoundingClientRect();
    const fallbackTop =
      document
        .querySelector<HTMLElement>(`[data-calendar-date="${date}"]`)
        ?.getBoundingClientRect().top ?? e.clientY;
    const y = e.clientY - (rect?.top ?? fallbackTop);
    return {
      value: yToZonedTime(y, targetDate, targetZone, gesture.current?.snap),
      date: targetDate,
      zone: targetZone,
    };
  };

  const previewTimes = (edge: ResizeEdge, movingTime: ZonedTimeString) => {
    let moving = movingTime;
    if (edge === "start" && epochMs(moving) >= epochMs(endTime)) {
      moving = addMinutes(endTime, -MIN_SPAN_MINUTES);
    }
    if (edge === "end" && epochMs(moving) <= epochMs(startTime)) {
      moving = addMinutes(startTime, MIN_SPAN_MINUTES);
    }
    const nextStart = edge === "start" ? moving : startTime;
    const nextEnd = edge === "end" ? moving : endTime;
    const geometry = blockGeometry(nextStart, nextEnd, date, zone);
    return {
      startTime: nextStart,
      endTime: nextEnd,
      top: geometry.top,
      height: geometry.height,
    };
  };

  const handlePointerDown = (edge: ResizeEdge) => (e: React.PointerEvent) => {
    if (disabled || e.button !== 0) return;
    // Keep dnd-kit's PointerSensor from seeing this gesture
    e.stopPropagation();
    e.preventDefault();

    gesture.current = {
      edge,
      snap:
        e.pointerType === "touch" ? SNAP_MINUTES_TOUCH : SNAP_MINUTES_POINTER,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    const initial = previewTimes(edge, edge === "start" ? startTime : endTime);
    setPreview(initial);
    onPreviewChange?.(initial);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const next = previewTimes(g.edge, pointerTime(e).value);
    setPreview(next);
    onPreviewChange?.(next);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;

    const target = pointerTime(e);
    const final = previewTimes(g.edge, target.value);
    gesture.current = null;
    setPreview(null);
    onPreviewChange?.(null);

    const unchanged =
      minutesBetween(startTime, final.startTime) === 0 &&
      minutesBetween(endTime, final.endTime) === 0;
    if (!unchanged) {
      onCommit(
        { startTime: final.startTime, endTime: final.endTime },
        {
          edge: g.edge,
          originalDisplayZone: zone,
          targetDisplayZone: target.zone,
          targetDate: target.date,
        },
      );
    }
  };

  const handlePointerCancel = () => {
    gesture.current = null;
    setPreview(null);
    onPreviewChange?.(null);
  };

  const handleProps = (edge: ResizeEdge) => ({
    onPointerDown: handlePointerDown(edge),
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
  });

  return { preview, resizing: preview !== null, handleProps };
}
